import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { theme } from '../../styles/theme';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { getUsers, activateUser, deactivateUser } from '../../api/admin';
import { formatDateTime } from '../../utils/formatting';
import CreateUserModal from './CreateUserModal';

const UserList = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch users
  const { data: users, isLoading, isError } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });
  
  // Activate user mutation
  const activateMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Benutzer erfolgreich aktiviert');
    },
  });
  
  // Deactivate user mutation
  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Benutzer erfolgreich deaktiviert');
    },
  });
  
  // Handle status toggle
  const handleStatusToggle = (userId, isActive) => {
    if (isActive) {
      deactivateMutation.mutate(userId);
    } else {
      activateMutation.mutate(userId);
    }
  };
  
  if (isLoading) {
    return <Loading text="Benutzer werden geladen..." />;
  }
  
  if (isError) {
    return <ErrorMessage>Fehler beim Laden der Benutzer.</ErrorMessage>;
  }
  
  return (
    <Container>
      <Header>
        <Title>Benutzerverwaltung</Title>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          Neuen Benutzer erstellen
        </Button>
      </Header>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Benutzername</TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>E-Mail</TableHeader>
              <TableHeader>Rolle</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Letzte Anmeldung</TableHeader>
              <TableHeader>Aktionen</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.firstName} {user.lastName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <RoleBadge role={user.role}>
                    {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                  </RoleBadge>
                </TableCell>
                <TableCell>
                  <StatusBadge isActive={user.isActive}>
                    {user.isActive ? 'Aktiv' : 'Inaktiv'}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  {user.lastLogin ? formatDateTime(user.lastLogin) : 'Nie'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant={user.isActive ? 'danger' : 'primary'}
                    onClick={() => handleStatusToggle(user.id, user.isActive)}
                    isLoading={
                      (activateMutation.isPending || deactivateMutation.isPending) &&
                      (activateMutation.variables === user.id || deactivateMutation.variables === user.id)
                    }
                  >
                    {user.isActive ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${theme.colors.heading};
  margin: 0;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.boxShadow.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${theme.colors.backgroundAlt};
  border-bottom: 1px solid ${theme.colors.border};
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${theme.colors.text};
`;

const TableBody = styled.tbody`
  & tr:nth-child(even) {
    background-color: ${theme.colors.backgroundAlt};
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${theme.colors.text};
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${({ role }) => 
    role === 'admin' ? `${theme.colors.secondary}20` : `${theme.colors.info}20`};
  color: ${({ role }) => 
    role === 'admin' ? theme.colors.secondary : theme.colors.info};
  border: 1px solid ${({ role }) => 
    role === 'admin' ? `${theme.colors.secondary}40` : `${theme.colors.info}40`};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${({ isActive }) => 
    isActive ? `${theme.colors.success}20` : `${theme.colors.danger}20`};
  color: ${({ isActive }) => 
    isActive ? theme.colors.success : theme.colors.danger};
  border: 1px solid ${({ isActive }) => 
    isActive ? `${theme.colors.success}40` : `${theme.colors.danger}40`};
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: ${theme.colors.danger}10;
  color: ${theme.colors.danger};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
`;

export default UserList;
